import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useReducer, useState } from 'react';
import { validateEmail } from 'src/components';
import {
  usePortalioConnect,
} from 'src/graphql/entities/integrations/portalio/hooks/use-portalio-connect';
import { useNotifications } from 'src/utils/hooks';

type Action =
  | { type: 'USERNAME_CHANGE'; payload: string }
  | { type: 'PASSWORD_CHANGE'; payload: string }
  | { type: 'RESET' }
  | { type: 'COMPLETE' }
  | { type: 'SUBMIT' };

type State = {
  userName: string;
  password: string;
  isConnected: boolean;
  isEditMode: boolean;
};

export const PortalioConnectIntegration = () => {
  const { showErrorAlert } = useNotifications();
  const theme = useTheme();
  const { connect } = usePortalioConnect();

  const [state, dispatch] = useReducer(
    (state: State, action: Action) => {
      switch (action.type) {
        case 'USERNAME_CHANGE':
          return {
            ...state,
            userName: action.payload,
          };
        case 'PASSWORD_CHANGE':
          return {
            ...state,
            password: action.payload,
          };
        case 'COMPLETE': {
          return {
            ...state,
            isConnected: true,
          };
        }
        case 'SUBMIT':
          return { ...state, isEditMode: true, userName: '', password: '' };
        case 'RESET':
          return { userName: '', password: '', isEditMode: false, isConnected: false };
        default:
          return state;
      }
    },
    { userName: '', password: '', isConnected: false, isEditMode: false },
  );
  const [isValidEmail, setIsValidEmail] = useState(true);
  const isValid = !!state.userName && !!state.password;

  const onConnectHandler = useCallback(async () => {
    if (!isValid) {
      return;
    }
    await connect({
      variables: {
        params: {
          userName: state.userName,
          password: state.password,
        },
      },
      onError: (error) => {
        showErrorAlert(error.message);
      },
    });
    dispatch({ type: 'RESET' });
    setIsValidEmail(true);
  }, [connect, isValid, showErrorAlert, state.password, state.userName]);

  const onCancelHandler = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  if (state.isEditMode) {
    return (
      <Card variant="outlined">
        <CardHeader
          avatar={<Avatar alt="portalio-logo" src="/portalio-logo.png" />}
          title="Connect Portal.io"
        />
        <CardContent
          sx={{
            paddingTop: 0,
          }}
        >
          <TextField
            required
            label="Email"
            type="email"
            size="small"
            placeholder="Email"
            error={!isValidEmail}
            helperText={!isValidEmail ? 'Invalid email' : undefined}
            onChange={(e) => dispatch({ type: 'USERNAME_CHANGE', payload: e.target.value })}
            onBlur={() => setIsValidEmail(!state.userName || validateEmail(state.userName))}
            sx={{
              width: '100%',
              marginTop: theme.spacing(),
              marginBottom: theme.spacing(2),
            }}
          />
          <TextField
            required
            label="Password"
            placeholder="Password"
            type="password"
            autoComplete="off"
            size="small"
            onChange={(e) => dispatch({ type: 'PASSWORD_CHANGE', payload: e.target.value })}
            sx={{
              width: '100%',
              marginTop: theme.spacing(),
              marginBottom: theme.spacing(2),
            }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your API key will be encrypted and stored securely.
          </Typography>
        </CardContent>
        <CardActions sx={{ m: theme.spacing(), justifyContent: 'end' }}>
          <Button size="small" variant="outlined" onClick={onCancelHandler}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onConnectHandler}
            disabled={!isValid}
          >
            Connect
          </Button>
        </CardActions>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<Avatar alt="portalio-logo" src="/portalio-logo.png" />}
        title="Portal.io"
        subheader="Not connected"
      />
      <CardContent>
        <Typography gutterBottom variant="body2">
          Import proposals from Portal.io into site files to generate rich site documentation
        </Typography>
      </CardContent>
      <CardActions sx={{ m: theme.spacing(), justifyContent: 'end' }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => dispatch({ type: 'SUBMIT' })}
          color="primary"
          fullWidth
        >
          Connect
        </Button>
      </CardActions>
    </Card>
  );
};
